/* eslint-disable react-refresh/only-export-components -- this module intentionally
   co-locates the session provider with its hooks/selectors (useSession, etc.). */
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { User } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { firebaseAuth } from './firebase';
import { supabase } from './supabase';
import { fetchMyAccess, fetchMyFeatures, type Access, type Restaurant } from './data';
import { applyFeatureFlags } from './access';
import Paywall from '@/components/feature/billing/Paywall';

/* Session = signed-in Firebase user + their restaurant (tenant).
   Wrap any authenticated surface in <RequireAuth>; inside it,
   useSession() / useRestaurant() / useAccess() are guaranteed non-null. */

interface SessionCtx {
  user: User;
  restaurant: Restaurant;
  access: Access;
  refreshRestaurant: () => Promise<void>;
  refreshAccess: () => Promise<void>;
}

const Ctx = createContext<SessionCtx | null>(null);

export function useSession(): SessionCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSession must be used inside <RequireAuth>');
  return v;
}

export const useRestaurant = () => useSession().restaurant;
export const useAccess = (): Access => useSession().access;

export function RequireAuth({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [access, setAccess] = useState<Access | null>(null);
  const [failed, setFailed] = useState('');

  useEffect(() => {
    const unsub = firebaseAuth.onAuthStateChanged(async (u) => {
      if (!u) {
        // Remember where they were headed (e.g. /dashboard/billing from the POS
        // "Upgrade" button) so login can return them there.
        const dest = window.location.pathname + window.location.search;
        const q = dest && dest !== '/dashboard' ? `?redirect=${encodeURIComponent(dest)}` : '';
        navigate(`/login${q}`, { replace: true });
        return;
      }
      setUser(u);
      try {
        const { data: m, error: mErr } = await supabase
          .from('merchants')
          .select('restaurantId')
          .eq('id', u.uid)
          .maybeSingle();
        if (mErr) throw mErr;
        const rid = (m?.restaurantId as string | undefined) ?? null;
        if (!rid) {
          navigate('/onboarding', { replace: true });
          return;
        }
        const { data: r, error: rErr } = await supabase
          .from('restaurants')
          .select('*')
          .eq('id', rid)
          .single();
        if (rErr) throw rErr;
        setRestaurant(r as Restaurant);
        // Superadmin-editable feature flags — hydrate before gating renders.
        // Non-fatal: on error we keep the hardcoded FEATURE_TIER defaults.
        try {
          applyFeatureFlags(await fetchMyFeatures());
        } catch { /* fall back to defaults */ }
        // Subscription/trial state — load after the restaurant resolves.
        const a = await fetchMyAccess();
        setAccess(a);
      } catch (e) {
        setFailed(e instanceof Error ? e.message : 'Could not load your restaurant.');
      }
    });
    return unsub;
  }, [navigate]);

  const refreshRestaurant = useCallback(async () => {
    const rid = restaurant?.id;
    if (!rid) return;
    const { data } = await supabase.from('restaurants').select('*').eq('id', rid).single();
    if (data) setRestaurant(data as Restaurant);
  }, [restaurant?.id]);

  const refreshAccess = useCallback(async () => {
    try {
      const a = await fetchMyAccess();
      setAccess(a);
    } catch { /* keep prior access on transient error */ }
  }, []);

  if (failed) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-background-50 px-6 text-center">
        <i className="ri-error-warning-line text-3xl text-accent-500" />
        <p className="text-sm text-foreground-700 max-w-md">{failed}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-md cursor-pointer"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!user || !restaurant || !access) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <i className="ri-loader-4-line text-2xl text-primary-500 animate-spin" />
      </div>
    );
  }

  const value: SessionCtx = { user, restaurant, access, refreshRestaurant, refreshAccess };

  // Trial/plan expired → full block. Provide context so the Paywall can read
  // the restaurant + refresh access after a successful upgrade.
  if (access.blocked) {
    return (
      <Ctx.Provider value={value}>
        <Paywall />
      </Ctx.Provider>
    );
  }

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
